import React, { useMemo, useState } from 'react';
import axios from 'axios';
import {
  useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Stack,
} from '@mui/material';
import { LocalizedButton as Button, LocalizedChip as Chip, LocalizedTextField as TextField } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import {
  AutoAwesome,
  Favorite,
  Work,
  AccountBalanceWallet,
  SelfImprovement,
  HealthAndSafety,
  Lock,
  Style
} from '@mui/icons-material';
import SEO from '../components/SEO';
import { useAuth } from '../contexts/AuthContext';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

const TAROT_MODULES = [
  { title: 'Daily Vedic Tarot', subtitle: 'One card guidance for today', spreadType: 'daily_card', category: 'general', premium: false, icon: <Style /> },
  { title: 'Ask Vedic Tarot', subtitle: 'Ask a question and draw one card', spreadType: 'one_card', category: 'general', premium: false, icon: <AutoAwesome /> },
  { title: 'Past Present Future', subtitle: '3-card spiritual guidance', spreadType: 'three_card', category: 'general', premium: false, icon: <AutoAwesome /> },
  { title: 'Love Vedic Tarot', subtitle: 'Relationship energy and direction', spreadType: 'love', category: 'love', premium: false, icon: <Favorite /> },
  { title: 'Career Vedic Tarot', subtitle: 'Career obstacle and advice', spreadType: 'career', category: 'career', premium: false, icon: <Work /> },
  { title: 'Finance Vedic Tarot', subtitle: 'Money energy and advice', spreadType: 'finance', category: 'finance', premium: false, icon: <AccountBalanceWallet /> },
  { title: 'Advanced Vedic Tarot', subtitle: '10-card deep reading', spreadType: 'celtic_cross', category: 'general', premium: true, icon: <SelfImprovement /> },
  { title: 'Kundli + Tarot Combined', subtitle: 'Premium astro-tarot guidance', spreadType: 'kundli_tarot_combined', category: 'spiritual', premium: true, icon: <Lock /> }
] as const;

const CATEGORIES = [
  { key: 'general', label: 'General', icon: <AutoAwesome fontSize="small" /> },
  { key: 'love', label: 'Love', icon: <Favorite fontSize="small" /> },
  { key: 'career', label: 'Career', icon: <Work fontSize="small" /> },
  { key: 'finance', label: 'Finance', icon: <AccountBalanceWallet fontSize="small" /> },
  { key: 'spiritual', label: 'Spiritual', icon: <SelfImprovement fontSize="small" /> },
  { key: 'health', label: 'Wellness', icon: <HealthAndSafety fontSize="small" /> }
] as const;

const DEVICE_ID_KEY = 'kundlistar_tarot_device_id';

const getDeviceId = () => {
  const existing = localStorage.getItem(DEVICE_ID_KEY);
  if (existing) return existing;
  const next = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  localStorage.setItem(DEVICE_ID_KEY, next);
  return next;
};

const normalizeTarotLanguage = (language?: string) => (
  String(language || '').toLowerCase().startsWith('hi') ? 'hi' : 'en'
);

const getTarotErrorMessage = (err: any) => {
  if (err.response?.data?.message) return err.response.data.message;
  if (err.response?.data?.error) return err.response.data.error;
  if (err.message === 'Network Error' || !err.response) {
    return `Could not connect to Kundlistar API at ${API_BASE_URL}. Please start the backend server and try again.`;
  }
  return err.message || 'Unable to draw Vedic Tarot cards.';
};

const VedicTarot: React.FC = () => {
  const { i18n } = useTranslation();
  const { state: authState } = useAuth();
  const [selected, setSelected] = useState<typeof TAROT_MODULES[number]>(TAROT_MODULES[0]);
  const [category, setCategory] = useState('general');
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reading, setReading] = useState<any>(null);

  const selectedCategory = useMemo(
    () => CATEGORIES.find((item) => item.key === category)?.label || 'General',
    [category]
  );
  const tarotLanguage = normalizeTarotLanguage(i18n.resolvedLanguage || i18n.language);

  const handleSelect = (item: typeof TAROT_MODULES[number]) => {
    setSelected(item);
    setCategory(item.category);
    setReading(null);
    setError('');
  };

  const drawCards = async () => {
    try {
      setLoading(true);
      setError('');
      setReading(null);
      const response = await axios.post(`${API_BASE_URL}/tarot/draw`, {
        spreadType: selected.spreadType,
        category,
        question,
        language: tarotLanguage
      }, {
        headers: {
          'x-device-id': getDeviceId(),
          'Accept-Language': tarotLanguage,
          ...(authState.token ? { Authorization: `Bearer ${authState.token}` } : {})
        }
      });
      setReading(response.data);
    } catch (err: any) {
      setError(getTarotErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <SEO
        title="Vedic Tarot - Spiritual Card Guidance | Kundlistar"
        description="Draw Vedic Tarot cards with planetary symbolism, category-based meanings, remedies, and safe spiritual guidance."
        keywords="Vedic Tarot, tarot astrology, Kundlistar Tarot, spiritual guidance cards"
      />

      <Stack spacing={1.5} sx={{ mb: 4 }}>
        {/* <Chip icon={<Style />} label="New Module" color="primary" sx={{ alignSelf: 'flex-start' }} /> */}
        <Typography variant="h3" component="h1" sx={{ fontWeight: 600, color: "var(--app-ink-primary)" }}>
          Vedic Tarot
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 780 }}>
          Draw cards for spiritual guidance with Vedic planetary mapping, tattva influence, simple remedies, and self-reflection.
        </Typography>
      </Stack>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Stack spacing={1.5}>
            {TAROT_MODULES.map((item) => {
              const active = selected.spreadType === item.spreadType;
              return (
                <Card
                  key={item.spreadType}
                  onClick={() => handleSelect(item)}
                  sx={{
                    cursor: 'pointer',
                    borderColor: active ? 'primary.main' : 'divider',
                    borderWidth: 1,
                    borderStyle: 'solid',
                    boxShadow: active ? 4 : 1
                  }}
                >
                  <CardContent>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box sx={{ color: "var(--app-ink-primary)", display: 'flex' }}>{item.icon}</Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{item.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{item.subtitle}</Typography>
                      </Box>
                      {item.premium && <Chip size="small" label="Premium" color="secondary" />}
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <Card>
            <CardContent>
              <Stack spacing={2.5}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>{selected.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{selected.subtitle}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Select Category</Typography>
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    {CATEGORIES.map((item) => (
                      <Chip
                        key={item.key}
                        icon={item.icon}
                        label={item.label}
                        clickable
                        color={category === item.key ? 'primary' : 'default'}
                        onClick={() => setCategory(item.key)}
                      />
                    ))}
                  </Stack>
                </Box>

                <TextField
                  label="Your Question"
                  placeholder="Example: Should I change my job?"
                  value={question}
                  onChange={(event) => setQuestion(event.target.value)}
                  multiline
                  minRows={3}
                  fullWidth
                />

                <Button
                  variant="contained"
                  size="large"
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Style />}
                  onClick={drawCards}
                  disabled={loading}
                >
                  {loading ? 'Drawing Cards...' : 'Draw Cards'}
                </Button>

                {error && (
                  <Alert severity={error.toLowerCase().includes('premium') ? 'warning' : 'error'}>
                    {error}
                  </Alert>
                )}

                <Alert severity="info">
                  Vedic Tarot is for spiritual guidance and self-reflection only. It is not a guaranteed prediction or professional advice.
                </Alert>
              </Stack>
            </CardContent>
          </Card>

          {reading && (
            <Stack spacing={2} sx={{ mt: 3 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {reading.language === 'hi' ? `${reading.categoryLabel || selectedCategory} रीडिंग` : `Your ${selectedCategory} Reading`}
                  </Typography>
                  {reading.question && (
                    <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                      {reading.language === 'hi' ? 'प्रश्न' : 'Question'}: {reading.question}
                    </Typography>
                  )}
                  {reading.questionInterpretation && (
                    <Alert
                      severity={reading.questionInterpretation.answerType === 'yes' ? 'success' : 'warning'}
                      sx={{ mt: 2, alignItems: 'flex-start' }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        {reading.language === 'hi' ? 'सीधा उत्तर' : 'Direct answer'}
                      </Typography>
                      <Typography sx={{ fontWeight: 700 }}>
                        {reading.questionInterpretation.verdict || reading.questionInterpretation.directGuidance}
                      </Typography>
                      {reading.questionInterpretation.reason && (
                        <Typography sx={{ mt: 0.75 }}>{reading.questionInterpretation.reason}</Typography>
                      )}
                      {reading.questionInterpretation.nextSteps?.[0] && (
                        <Typography sx={{ mt: 0.75 }}>
                          <strong>{reading.language === 'hi' ? 'अगला कदम' : 'Next step'}:</strong>{' '}
                          {reading.questionInterpretation.nextSteps[0]}
                        </Typography>
                      )}
                    </Alert>
                  )}
                  {reading.basicSummary && <Typography sx={{ mt: 1.5 }}>{reading.basicSummary}</Typography>}
                </CardContent>
              </Card>

              {reading.cards?.map((card: any, index: number) => (
                <Card key={`${card.key}-${index}`}>
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                        <Box>
                          <Typography variant="overline" color="text.secondary">{card.position}</Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>{card.displayName}</Typography>
                        </Box>
                        <Chip label={card.orientationLabel || (card.orientation === 'upright' ? 'Upright' : 'Reversed')} color="primary" variant="outlined" />
                      </Stack>
                      <Typography color="text.secondary">{card.keywords?.join(', ')}</Typography>
                      <Typography>{card.meaning}</Typography>
                      <Divider />
                      <Grid container spacing={1.5}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="subtitle2">{reading.language === 'hi' ? 'वैदिक मैपिंग' : 'Vedic Mapping'}</Typography>
                          <Typography variant="body2">{reading.language === 'hi' ? 'ग्रह' : 'Planet'}: {card.vedicMapping?.planet || '-'}</Typography>
                          <Typography variant="body2">{reading.language === 'hi' ? 'तत्व' : 'Tattva'}: {card.vedicMapping?.tattva || '-'}</Typography>
                          <Typography variant="body2">{reading.language === 'hi' ? 'देवता' : 'Deity'}: {card.vedicMapping?.deity || '-'}</Typography>
                          <Typography variant="body2">{reading.language === 'hi' ? 'चक्र' : 'Chakra'}: {card.vedicMapping?.chakra || '-'}</Typography>
                          <Typography variant="body2">{reading.language === 'hi' ? 'मंत्र' : 'Mantra'}: {card.vedicMapping?.mantra || '-'}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <Typography variant="subtitle2">{reading.language === 'hi' ? 'सरल उपाय' : 'Simple Remedy'}</Typography>
                          <Typography variant="body2">{card.remedy?.simple || (reading.language === 'hi' ? 'शांत रहें और जागरूकता के साथ कदम बढ़ाएं।' : 'Stay calm and act with awareness.')}</Typography>
                          {card.remedy?.mantra && <Typography variant="body2">{card.remedy.mantra}</Typography>}
                        </Grid>
                      </Grid>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default VedicTarot;
